import { T, tt } from '@agos/shared'
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react'
import { useEffect, useState } from 'react'

import { FIELDS, anyField, anyList, LISTS, safeAsset, type Section } from './site'

/**
 * The gallery as legacy draws it - SAAS.md §4.4, FB-489.
 *
 * **The §12.3 pair is what made this a defect rather than a preference.** Legacy shows one large
 * featured image with a counter, arrows, a thumbnail strip and a full-screen lightbox; AGOS showed a
 * grid of all nineteen. A grid is not *wrong* for a visitor, and §4 does not leave that open: *"this
 * is the one app where 'layout similar' means the PUBLIC design stays exactly as-is."*
 *
 * **Its own file because it is the only section with state**, and a component file that exports a
 * hook-using component beside pure renderers is how fast-refresh boundaries get muddled.
 *
 * Ported behaviour, each of which is legacy's and not invented:
 *
 * - the index **wraps** both ways, so *previous* on the first image lands on the last;
 * - the index is **clamped** rather than trusted, which is what stops a re-publish that shortens the
 *   list from rendering `undefined` at the old position;
 * - the keyboard listener exists **only while the lightbox is open**, and is removed on close - a
 *   global `keydown` left behind would swallow arrow keys for the rest of the page;
 * - every thumbnail stays in the DOM, which is legacy's own note: *"users see the full set at a
 *   glance."*
 */
export function GallerySection({ section }: { section: Section }) {
  const items = anyList(section, LISTS.items)
  const images = items
    .map((item) => ({
      src: safeAsset(item.src),
      alt: anyField(item, FIELDS.alt),
      caption: anyField(item, FIELDS.caption),
    }))
    .filter((image): image is { src: string; alt: string; caption: string } => image.src !== null)

  const [active, setActive] = useState(0)
  const [lightbox, setLightbox] = useState(false)

  // Clamped, not trusted: the editor can publish a shorter list than the one on screen, and reading
  // past the end would render `undefined.src`.
  const index = images.length === 0 ? 0 : Math.min(active, images.length - 1)
  const current = images[index]

  useEffect(() => {
    if (!lightbox) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLightbox(false)
      else if (event.key === 'ArrowLeft') setActive((was) => step(was, -1, images.length))
      else if (event.key === 'ArrowRight') setActive((was) => step(was, 1, images.length))
    }
    window.addEventListener('keydown', onKey)
    // Removed on close as well as on unmount. A listener left behind would take the arrow keys off
    // the rest of the page, which is a bug nothing on this screen would show.
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox, images.length])

  if (current === undefined) return null

  const move = (direction: -1 | 1) => setActive((was) => step(was, direction, images.length))
  const many = images.length > 1

  return (
    <>
      <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
        <button
          type="button"
          onClick={() => setLightbox(true)}
          data-gallery-open
          className="block aspect-[16/9] w-full cursor-zoom-in sm:aspect-[2/1]"
          aria-label={tt('Otvoriť obrázok na celú obrazovku')}
        >
          <img
            src={current.src}
            alt={current.alt}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
          <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-lg bg-black/55 px-2.5 py-1.5 text-xs font-bold text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
            <ZoomIn className="h-3.5 w-3.5" /> <T>Zväčšiť</T>
          </span>
          <span
            data-gallery-counter
            className="absolute left-3 top-3 rounded-lg bg-black/55 px-2.5 py-1.5 text-xs font-bold tabular-nums text-white backdrop-blur-sm"
          >
            {index + 1} / {images.length}
          </span>
        </button>

        {many && (
          <>
            <button
              type="button"
              onClick={() => move(-1)}
              data-gallery-prev
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/80"
              aria-label={tt('Predchádzajúci obrázok')}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => move(1)}
              data-gallery-next
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/80"
              aria-label={tt('Ďalší obrázok')}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {current.caption !== '' && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-4 sm:p-6">
            <p
              className="whitespace-pre-line text-sm text-white/90 sm:text-base"
              data-gallery-caption
            >
              {current.caption}
            </p>
          </div>
        )}
      </div>

      {many && (
        <div className="-mx-1 mt-3 overflow-x-auto px-1">
          <div className="flex min-w-min gap-2">
            {images.map((image, position) => (
              <button
                key={position}
                type="button"
                onClick={() => setActive(position)}
                data-gallery-thumb={position}
                data-gallery-current={position === index ? 'true' : 'false'}
                className={
                  position === index
                    ? 'relative aspect-square w-24 shrink-0 overflow-hidden rounded-lg border-2 border-orange-500 ring-2 ring-orange-500/30 transition sm:w-28'
                    : 'relative aspect-square w-24 shrink-0 overflow-hidden rounded-lg border-2 border-white/10 opacity-70 transition hover:border-white/30 hover:opacity-100 sm:w-28'
                }
                aria-label={`${tt('Zobraziť obrázok')} ${position + 1}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 p-4 backdrop-blur-sm sm:p-8"
          onClick={() => setLightbox(false)}
          data-gallery-lightbox
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              setLightbox(false)
            }}
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label={tt('Zavrieť')}
          >
            <X className="h-6 w-6" />
          </button>

          <div className="absolute left-4 top-4 rounded-full bg-white/10 px-3 py-1.5 text-sm font-bold tabular-nums text-white/70">
            {index + 1} / {images.length}
          </div>

          {many && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                move(-1)
              }}
              className="absolute left-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25 sm:left-6"
              aria-label={tt('Predchádzajúci')}
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
          )}

          <img
            src={current.src}
            alt={current.alt}
            onClick={(event) => event.stopPropagation()}
            className="max-h-[78vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
          />

          {many && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                move(1)
              }}
              className="absolute right-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25 sm:right-6"
              aria-label={tt('Ďalší')}
            >
              <ChevronRight className="h-7 w-7" />
            </button>
          )}

          {current.caption !== '' && (
            <p
              onClick={(event) => event.stopPropagation()}
              className="mt-5 max-w-3xl whitespace-pre-line text-center text-sm text-white/90 sm:text-base"
            >
              {current.caption}
            </p>
          )}
        </div>
      )}
    </>
  )
}

/**
 * One step through the list, wrapping both ways.
 *
 * Exported so the wrap can be tested without rendering: *previous* on the first image landing on the
 * last is legacy's behaviour and the arithmetic that gets it wrong (`(i - 1) % n`) returns `-1` in
 * JavaScript rather than the last index.
 */
export function step(from: number, direction: -1 | 1, length: number): number {
  if (length <= 0) return 0
  const clamped = Math.min(Math.max(from, 0), length - 1)
  return (clamped + direction + length) % length
}
